/**
 * PrivacyRightsReviewStep — Session 2b (2026-07-16)
 *
 * Merchant-facing step 3 of onboarding: after a merchant submits their
 * privacy policy text and profile, we call the AI extractor and show them
 * a Yes/No/Unclear answer per right — with the exact supporting quote pulled
 * from their own policy. They confirm or override, then submit. Answers
 * persist as PrivacySignal objects with a `source` field so the accountant-
 * facing Dashboard can show "AI Confirmed" vs "Legacy Match" badges.
 *
 * Behaviour rules (per Session 2b spec):
 *   - AI-detected Yes/No values pre-fill the toggles as `source=ai_extracted`.
 *   - AI-detected `Unclear` values do NOT pre-fill — merchant must actively
 *     pick Yes or No. Submit is hard-blocked until every Unclear is answered.
 *   - Bulk "Confirm all as shown" action for merchants happy with all
 *     non-Unclear detections.
 *   - Merchants who change any answer stamp that right as
 *     `source=merchant_overridden`; unchanged rights become `merchant_confirmed`.
 *
 * Zero regression: this component is inert if not rendered. Onboarding.jsx
 * gates the render on the merchant having at least one selected state with
 * `scoring_policy=ACTIVE`. Merchants with only NY/TX/FL/DC selected skip
 * this step and land on the confirmation screen directly.
 */
import { useEffect, useMemo, useState } from "react";
import { Button } from "../ui/button";
import { Loader2, Sparkles, CheckCircle2, XCircle, HelpCircle, ArrowRight, Info } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

// Human-readable metadata for each right. Keys align with the backend
// `has_*` field names on merchant_profiles.privacy_signals.
const RIGHTS_META = [
  { key: "has_policy",      label: "Coherent privacy policy",     hint: "The document itself is a real privacy policy — not a placeholder or a copy-paste of a template with no substance." },
  { key: "has_opt_out",     label: "Opt-out of data sale/sharing", hint: "Consumers can explicitly opt out of the sale, sharing, or targeted-advertising use of their personal data." },
  { key: "has_delete",      label: "Right to delete",              hint: "Consumers can request deletion or erasure of their personal data." },
  { key: "has_access",      label: "Right to access",              hint: "Consumers can request a copy of, or view, the personal data you hold about them." },
  { key: "has_correction",  label: "Right to correction",          hint: "Consumers can request correction of inaccurate personal data — not just update their account settings." },
  { key: "has_portability", label: "Data portability",             hint: "Consumers can obtain their personal data in a portable, machine-readable format (CSV, JSON, etc.)." },
  { key: "has_sensitive_data_disclosure", label: "Sensitive data handling", hint: "Your policy discloses how you handle sensitive personal data (biometric, health, precise location, financial, etc.) — or affirms you don't collect any." },
];

export default function PrivacyRightsReviewStep({ policyText, onBack, onComplete }) {
  const [loading, setLoading] = useState(true);
  const [extraction, setExtraction] = useState(null);
  const [err, setErr] = useState(null);
  const [saving, setSaving] = useState(false);

  // Per-right merchant answer: { value: true|false|null, source: string }
  // null value means "not yet answered" (Unclear rights start here).
  const [answers, setAnswers] = useState({});

  useEffect(() => {
    (async () => {
      setLoading(true);
      setErr(null);
      try {
        const { data } = await api.post("/audit/extract-signals", {
          policy_text: policyText,
          use_cache: true,
        });
        setExtraction(data);
        // Seed answers from AI. Yes/No pre-fill as ai_extracted; Unclear
        // leaves value=null so merchant must actively decide.
        const seeded = {};
        for (const r of data.rights || []) {
          if (r.answer === "yes") {
            seeded[`has_${r.right === "sensitive" ? "sensitive_data_disclosure" : r.right}`] =
              { value: true, source: "ai_extracted", ai_quote: r.supporting_quote };
          } else if (r.answer === "no") {
            seeded[`has_${r.right === "sensitive" ? "sensitive_data_disclosure" : r.right}`] =
              { value: false, source: "ai_extracted", ai_quote: r.supporting_quote };
          } else {
            // Unclear — leave unanswered
            seeded[`has_${r.right === "sensitive" ? "sensitive_data_disclosure" : r.right}`] =
              { value: null, source: "ai_extracted", ai_quote: null };
          }
        }
        setAnswers(seeded);
      } catch (e) {
        setErr(e?.response?.data?.detail || "Extraction failed. Please try again.");
      } finally {
        setLoading(false);
      }
    })();
  }, [policyText]);

  const setAnswer = (rightKey, newValue) => {
    setAnswers((prev) => {
      const current = prev[rightKey] || { value: null, source: "ai_extracted", ai_quote: null };
      // If merchant changes value away from what AI originally said, stamp
      // as merchant_overridden. If merchant explicitly picks the AI's answer
      // (or answers an Unclear), stamp as merchant_confirmed / _answered.
      let source;
      const aiOriginal = current.source === "ai_extracted" ? current.value : null;
      if (aiOriginal === null) {
        source = "merchant_answered"; // was Unclear
      } else if (newValue === aiOriginal) {
        source = "merchant_confirmed";
      } else {
        source = "merchant_overridden";
      }
      return { ...prev, [rightKey]: { ...current, value: newValue, source } };
    });
  };

  const confirmAll = () => {
    setAnswers((prev) => {
      const next = { ...prev };
      for (const [key, sig] of Object.entries(prev)) {
        if (sig.value !== null && sig.source === "ai_extracted") {
          next[key] = { ...sig, source: "merchant_confirmed" };
        }
      }
      return next;
    });
    toast.success("All AI-detected answers confirmed. Review any Unclear rights below.");
  };

  const unansweredRights = useMemo(() => {
    return RIGHTS_META.filter(({ key }) => (answers[key]?.value ?? null) === null);
  }, [answers]);

  const submit = async () => {
    if (unansweredRights.length > 0) {
      toast.error(
        `Please answer the ${unansweredRights.length} right${unansweredRights.length === 1 ? "" : "s"} the AI wasn't sure about before continuing.`
      );
      return;
    }
    setSaving(true);
    try {
      // Only send rights the merchant actually engaged with (i.e., value !== null).
      const payload = {};
      for (const [key, sig] of Object.entries(answers)) {
        if (sig.value === null) continue;
        payload[key] = {
          value: sig.value,
          source: sig.source || "merchant_confirmed",
          ai_quote: sig.ai_quote || null,
        };
      }
      await api.post("/audit/me/privacy-signals", { signals: payload });
      toast.success("Privacy rights saved. Your next audit will reflect these answers.");
      onComplete?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-lg border border-[var(--rg-border)] bg-[var(--rg-surface)] p-6 space-y-5" data-testid="step-rights-review">
      <div>
        <div className="flex items-center gap-2 mb-1">
          <Sparkles className="w-4 h-4 text-[var(--rg-sage)]" />
          <h2 className="font-heading font-bold text-2xl text-[var(--rg-text-strong)]">Step 4 · Review privacy rights</h2>
        </div>
        <p className="text-[var(--rg-text)] text-sm leading-snug" data-testid="rights-review-intro">
          We reviewed your privacy policy and found the rights below.
          Takes about 30 seconds to confirm — most merchants can click <span className="text-[var(--rg-text-strong)] font-semibold">&ldquo;Confirm all&rdquo;</span> if the AI got everything right.
        </p>
      </div>

      {loading && (
        <div className="flex items-center gap-3 text-[var(--rg-text)] py-6" data-testid="rights-review-loading">
          <Loader2 className="w-5 h-5 animate-spin text-[var(--rg-sage)]" />
          <span>Reviewing your policy…</span>
        </div>
      )}

      {err && !loading && (
        <div className="rounded-md border border-[var(--rg-red)] bg-[var(--rg-red-soft)] p-3 text-sm text-[var(--rg-text-strong)]" data-testid="rights-review-error">
          {err}
        </div>
      )}

      {!loading && !err && extraction && (
        <>
          {/* Confirm All bulk action — top-right, always visible while there are ai_extracted rights */}
          <div className="flex items-center justify-between border-b border-[var(--rg-border)] pb-3">
            <div className="text-[11px] text-[var(--rg-muted)] italic" data-testid="rights-review-source-tag">
              {extraction.source === "ai" && "AI reviewed your policy just now."}
              {extraction.source === "cache" && "AI review loaded from cache (same policy text)."}
              {/* 2026-08-18 — Fallback is now a genuine edge case (Claude
                  timeout / parse failure), not the default state. Copy
                  updated to reflect that: previously said "AI unavailable"
                  which implied a transient outage even when EXTRACTOR_MODE
                  was set to keyword_only by design. */}
              {extraction.source === "fallback" && "AI review couldn't parse this policy — showing keyword-matcher estimates as a backup. Please confirm or override each answer."}
            </div>
            <Button
              size="sm"
              onClick={confirmAll}
              className="bg-[var(--rg-sage)] text-[var(--rg-on-sage)] hover:bg-[var(--rg-sage-hover)] font-semibold text-xs"
              data-testid="rights-review-confirm-all"
            >
              <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Confirm all as shown
            </Button>
          </div>

          {/* Per-right rows */}
          <div className="space-y-2" data-testid="rights-review-grid">
            {RIGHTS_META.map(({ key, label, hint }) => {
              const sig = answers[key] || { value: null, source: "ai_extracted", ai_quote: null };
              const isUnclear = sig.value === null;
              const isOverridden = sig.source === "merchant_overridden";
              return (
                <div
                  key={key}
                  className={`rounded-md border p-3 ${
                    isUnclear ? "border-[var(--rg-amber)] bg-[var(--rg-amber-soft)]" : "border-[var(--rg-border)] bg-[var(--rg-surface)]"
                  }`}
                  data-testid={`right-row-${key}`}
                >
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="flex-1 min-w-[240px]">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[var(--rg-text-strong)] font-semibold text-sm">{label}</span>
                        {sig.source === "ai_extracted" && !isUnclear && (
                          <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-[var(--rg-sage)] text-[var(--rg-text-strong)]">
                            AI Detected
                          </span>
                        )}
                        {sig.source === "merchant_confirmed" && (
                          <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-[var(--rg-sage)] text-[var(--rg-on-sage)]">
                            You confirmed
                          </span>
                        )}
                        {isOverridden && (
                          <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-[var(--rg-amber)] text-[var(--rg-on-sage)]">
                            You overrode
                          </span>
                        )}
                        {sig.source === "merchant_answered" && (
                          <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-[var(--rg-sage)] text-[var(--rg-on-sage)]">
                            You answered
                          </span>
                        )}
                        {isUnclear && (
                          <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-[var(--rg-amber)] text-[var(--rg-on-sage)]">
                            Please answer
                          </span>
                        )}
                      </div>
                      <div className="text-[11px] text-[var(--rg-muted)] mt-1 flex items-start gap-1">
                        <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
                        <span>{hint}</span>
                      </div>
                      {sig.ai_quote && (
                        <div className="mt-2 text-[11px] text-[var(--rg-text)] italic border-l-2 border-[var(--rg-sage)] pl-2" data-testid={`right-quote-${key}`}>
                          {`"${sig.ai_quote}"`}
                        </div>
                      )}
                    </div>

                    {/* Yes / No toggle */}
                    <div className="flex gap-1.5" data-testid={`right-toggle-${key}`}>
                      <button
                        type="button"
                        onClick={() => setAnswer(key, true)}
                        className={`text-xs font-semibold px-3 py-1.5 rounded border transition-fast ${
                          sig.value === true
                            ? "bg-[var(--rg-sage)] text-[var(--rg-on-sage)] border-[var(--rg-sage)]"
                            : "bg-transparent text-[var(--rg-muted)] border-[var(--rg-border)] hover:border-[var(--rg-sage)] hover:text-[var(--rg-text-strong)]"
                        }`}
                        data-testid={`right-yes-${key}`}
                      >
                        <CheckCircle2 className="w-3.5 h-3.5 inline mr-1" /> Yes
                      </button>
                      <button
                        type="button"
                        onClick={() => setAnswer(key, false)}
                        className={`text-xs font-semibold px-3 py-1.5 rounded border transition-fast ${
                          sig.value === false
                            ? "bg-[var(--rg-red)] text-[var(--rg-text-strong)] border-[var(--rg-red)]"
                            : "bg-transparent text-[var(--rg-muted)] border-[var(--rg-border)] hover:border-[var(--rg-red)] hover:text-[var(--rg-text-strong)]"
                        }`}
                        data-testid={`right-no-${key}`}
                      >
                        <XCircle className="w-3.5 h-3.5 inline mr-1" /> No
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Unanswered warning */}
          {unansweredRights.length > 0 && (
            <div className="rounded-md border border-[var(--rg-amber)] bg-[var(--rg-amber-soft)] p-3 text-xs text-[var(--rg-text-strong)]" data-testid="rights-review-blocker">
              <HelpCircle className="w-4 h-4 inline mr-1 text-[var(--rg-amber)]" />
              <strong>{unansweredRights.length}</strong> right{unansweredRights.length === 1 ? "" : "s"} still need{unansweredRights.length === 1 ? "s" : ""} your answer before you can continue.
              The AI wasn&apos;t confident enough to guess these based on your policy — your call is required.
            </div>
          )}

          <div className="flex justify-between pt-2">
            <Button variant="outline" onClick={onBack} className="border-[var(--rg-border)] text-[var(--rg-text-strong)] hover:bg-[var(--rg-surface)]" data-testid="rights-review-back">
              Back
            </Button>
            <Button
              onClick={submit}
              disabled={saving || unansweredRights.length > 0}
              className="bg-[var(--rg-sage)] text-[var(--rg-on-sage)] hover:bg-[var(--rg-sage-hover)] font-bold disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="rights-review-submit"
            >
              {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</> : <>Save & continue <ArrowRight className="w-4 h-4 ml-2" /></>}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
